import { clearUserInfo, selectUserInfo } from "features/user/userSlice";
import {
  Box,
  Divider,
  Grid,
  IconButton,
  TextField,
  Typography,
} from "@mui/material";
import { Controller, useFieldArray, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { BusinessProfileService } from "subModule/src/core/services";
import AlertMessages from "common/Alert/AlertMessages";
import { useSnackbar } from "notistack";
import SimpleModal from "common/Form/Form";
import { useNavigate } from "react-router-dom";
import ComponentHeaderTwo from "common/ComponentHeaderTwo/ComponentHeaderTwo";
import { ReactComponent as Accounts } from "../../assets/SideBarIcons/accounts.svg";
import { useEffect, useRef, useState } from "react";
import CommonCheckbox from "common/CheckBox/Checkbox";
import DynamicSelect from "common/Select/Select";
import Currency from "common/Others/Currency";
import Language from "common/Others/Language";
import { useGetItemGroupDropdownService } from "subModule/src/services/itemgroup/useItemGroup";
import { useGetTaxProcedureDropdownService } from "subModule/src/services/taxProcedure/useTaxProcedure";
import Loader from "layout/Loader";
import AccountSettingsTabs from "./AccountSettingsTabs";
import FormDialog from "common/Modal/Modal";
import { useAuthLogoutService } from "subModule/src/services/userProfile/useProfile";
import { themes } from "theme/themes";
import { brandingInfo } from "features/server/brandingSlice";
import { Add, Remove } from "@mui/icons-material";
import { useGetBankAccountsDropdownService } from "subModule/src/services/bankaccount/useBankAccount";
import { useGetVendorService } from "subModule/src/services/vendor/useVendor";
import { clearSubscriptionStatuses } from "features/subscription/subscriptionSlice";

interface FormData {
  [key: string]: any;
}
export default function BusinessSettings() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const branding = useSelector(brandingInfo);
  const debounceTimer = useRef<NodeJS.Timeout | null>(null);
  const themeColors =
    branding?.attributes?.themeId === 100
      ? branding?.attributes?.customTheme
      : (themes as any)[branding?.attributes?.themeId || 3];
  const {
    register,
    control,
    handleSubmit,
    formState: { errors, dirtyFields, isDirty },
    reset,
    watch,
    setValue,
  } = useForm<FormData>({
    defaultValues: {
      sellBelowStock: true,
    },
  });
  const { fields, append, remove } = useFieldArray({
    control,
    name: "vehiclePlateFormat",
  });
  const [updateBusinessModal, setUpdateBusinessModal] = useState(false);
  const [loading, setLoading] = useState<boolean>(true);
  const [business, setBusiness] = useState<any>({});
  const [dropdownSearchTerm, setDropdownSearchTerm] = useState<any>("");
  const [pageNo, setPageNo] = useState<number>(1);
  const [searchLoading, setSearchLoading] = useState<string | null>(null);
  const loggedInUserData = useSelector(selectUserInfo);
  const primaryLanguage = loggedInUserData?.business?.primaryLanguage;
  const secondaryLanguage = loggedInUserData?.business?.secondaryLanguage;
  const [IsBtnEnabled, setIsBtnEnabled] = useState<boolean>(false);
  const { data: scrapItemGroupsData, refetch: scrapItemGroupsRefetch } =
    useGetItemGroupDropdownService({
      pageNumber: pageNo,
      size: 50,
      title: dropdownSearchTerm,
    });
  const { data: taxProceduresData, refetch: taxProceduresRefetch } =
    useGetTaxProcedureDropdownService({
      pageNumber: pageNo,
      size: 50,
      title: dropdownSearchTerm,
    });
  const { data: bankAccountsData, refetch: bankAccountsRefetch } =
    useGetBankAccountsDropdownService({
      pageNumber: pageNo,
      size: 50,
      search: dropdownSearchTerm,
    });
  const { data: vendorsData, refetch: vendorsRefetch } = useGetVendorService({
    pageNumber: pageNo,
    size: 50,
    title: dropdownSearchTerm,
  });
  const scrapItemGroups = (
    scrapItemGroupsData?.pages[0]?.data?.content || []
  ).map((item: { id: number; title: string }) => ({
    value: item.id,
    label: item.title,
  }));
  const taxProcedures = (taxProceduresData?.pages[0]?.data?.content || []).map(
    (item: { id: number; title: string }) => ({
      value: item.id,
      label: item.title,
    }),
  );
  const supplierOptions = (vendorsData?.pages[0]?.data?.content || []).map(
    (item: { id: number; title: string }) => ({
      value: item.id,
      label: item.title,
    }),
  );
  const bankAccounts = (bankAccountsData?.pages[0]?.data?.content || []).map(
    (item: { id: number; accountTitle: string }) => ({
      value: item.id,
      label: item.accountTitle,
    }),
  );
  const currencyOptions = Currency.map(
    (item: { value: string; title: string }) => ({
      value: item.value,
      label: item.title,
    }),
  );
  const languageOptions = Language.map(
    (item: { value: string; title: string }) => ({
      value: item.value,
      label: item.title,
    }),
  );
  const dataTypes = [
    { value: "number", label: t("number") },
    { value: "text", label: t("text") },
  ];
  const regionOptions = [
    { label: t("unitedArabEmirates"), value: "uae" },
    { label: t("saudiArabia"), value: "saudia" },
    { label: t("egypt"), value: "egypt" },
    { label: t("custom"), value: "custom" },
  ];
  const regionFormatMap = {
    uae: [
      {
        title: "Series",
        type: { value: "text", label: "text" },
        min: 1,
        max: 2,
      },
      {
        title: "Number",
        type: { value: "number", label: "number" },
        min: 1,
        max: 5,
      },
    ],
    saudia: [
      { title: "Code", type: { value: "text", label: "text" }, min: 3, max: 3 },
      {
        title: "Number",
        type: { value: "number", label: "number" },
        min: 1,
        max: 4,
      },
    ],
    egypt: [
      { title: "Code", type: { value: "text", label: "text" }, min: 3, max: 3 },
      {
        title: "Number",
        type: { value: "number", label: "number" },
        min: 1,
        max: 4,
      },
    ],
    custom: [
      {
        title: "Plate Number",
        type: { value: "text", label: "text" },
        min: 1,
        max: 15,
      },
    ],
  };
  const timeZones = (Intl as any).supportedValuesOf?.("timeZone") ?? [];
  const timeZonesList = timeZones.map((tz: string) => ({
    value: tz,
    label: tz,
  }));
  useEffect(() => {
    scrapItemGroupsRefetch();
    taxProceduresRefetch();
    vendorsRefetch();
    bankAccountsRefetch();
  }, []);
  useEffect(() => {
    if (scrapItemGroupsData && taxProceduresData) {
      reset({
        ...(loggedInUserData?.business || {}),
        primaryLanguage: loggedInUserData?.business?.primaryLanguage
          ? {
              value: loggedInUserData?.business?.primaryLanguage,
              label: loggedInUserData?.business?.primaryLanguage,
            }
          : null,
        secondaryLanguage: loggedInUserData?.business?.secondaryLanguage
          ? {
              value: loggedInUserData?.business?.secondaryLanguage,
              label: loggedInUserData?.business?.secondaryLanguage,
            }
          : null,
        scrapItemGroupId: loggedInUserData?.business?.scrapItemGroupId
          ? {
              value: loggedInUserData?.business?.scrapItemGroupId,
              label:
                loggedInUserData?.business?.scrapItemGroupTitle ||
                loggedInUserData?.business?.scrapItemGroupId,
            }
          : null,
        taxProcedureId: loggedInUserData?.business?.taxProcedureId
          ? {
              value: loggedInUserData?.business?.taxProcedureId,
              label:
                loggedInUserData?.business?.taxProcedureTitle ||
                loggedInUserData?.business?.taxProcedureId,
            }
          : null,
        bankAccountId: loggedInUserData?.business?.bankAccountId
          ? {
              value: loggedInUserData?.business?.bankAccountId,
              label:
                loggedInUserData?.business?.bankAccountTitle ||
                loggedInUserData?.business?.bankAccountId,
            }
          : null,
        vendorId: loggedInUserData?.business?.vendorId
          ? {
              value: loggedInUserData?.business?.vendorId,
              label:
                loggedInUserData?.business?.vendorName ||
                loggedInUserData?.business?.vendorId,
            }
          : null,
        currency: loggedInUserData?.business?.currency
          ? {
              value: loggedInUserData?.business?.currency,
              label:
                currencyOptions.find(
                  (v) => v.value === loggedInUserData?.business?.currency,
                )?.label || loggedInUserData?.business?.currency,
            }
          : null,
        zone: loggedInUserData?.business?.zone
          ? {
              value: loggedInUserData?.business?.zone,
              label:
                currencyOptions.find(
                  (v) => v.value === loggedInUserData?.business?.zone,
                )?.label || loggedInUserData?.business?.zone,
            }
          : null,
        vehiclePlateFormatRegion: loggedInUserData?.business?.vehiclePlateFormat
          ?.region
          ? {
              label:
                regionOptions.find(
                  (v) =>
                    v.value ===
                    loggedInUserData?.business?.vehiclePlateFormat?.region,
                )?.label ||
                loggedInUserData?.business?.vehiclePlateFormat?.region,
              value: loggedInUserData?.business?.vehiclePlateFormat?.region,
            }
          : { label: t("unitedArabEmirates"), value: "uae" },
        vehiclePlateFormat:
          loggedInUserData?.business?.vehiclePlateFormat?.formats?.map(
            (item: any) => ({
              ...item,
              type: { value: item.type, label: t(item.type) },
            }),
          ) ||
          (regionFormatMap as any)[
            loggedInUserData?.business?.vehiclePlateFormat?.region || "uae"
          ].map((item: any) => ({
            ...item,
            type: { value: item.type.value, label: t(item.type.value) },
          })),
      });
      setLoading(false);
    }
  }, [scrapItemGroupsData, taxProceduresData]);
  useEffect(() => {
    if (isDirty) setIsBtnEnabled(true);
  }, [isDirty, dirtyFields]);
  const generatePlatePreview = (lang: "en" | "ar") => {
    const formatFields = watch("vehiclePlateFormat") || [];
    const parts = formatFields.map((field: any) => {
      const max = Number(field?.max || 1);

      const sample =
        field?.type?.value === "number"
          ? (lang === "en" ? "2" : "٢").repeat(max)
          : Array(max)
              .fill(lang === "en" ? "A" : "ا")
              .join("");
      return sample;
    });
    const result = parts.join(" ");

    return lang === "ar" ? result.split("").reverse().join(" ") : result;
  };
  const onSubmit = (data: any) => {
    delete data.privileges;
    delete data.type;
    delete data.username;
    delete data.email;
    delete data.priceListTitle;
    setIsBtnEnabled(false);
    setUpdateBusinessModal(true);
    setBusiness({
      ...data,
      vehiclePlateFormat: {
        region: data?.vehiclePlateFormatRegion?.value,
        formats: data.vehiclePlateFormat.map((item: any) => {
          const type = item.type?.value || item.type;
          return {
            title: item.title,
            type,
            min: Number(item.min),
            max: Number(item.max),
          };
        }),
      },
      primaryLanguage: data.primaryLanguage?.value || "",
      secondaryLanguage: data.secondaryLanguage?.value || "",
      currency: data.currency?.value || "",
      scrapItemGroupId: data.scrapItemGroupId.value,
      taxProcedureId: data.taxProcedureId?.value || 0,
      bankAccountId: data.bankAccountId?.value || null,
      vendorId: data.vendorId?.value || null,
      zone: data?.zone?.value || "",
    });
  };
  const { onLogOut } = useAuthLogoutService({
    onSuccess: (response) => {
      if (response.success) {
        dispatch(clearUserInfo());
        dispatch(clearSubscriptionStatuses());
        ["userInfo", "selectedSubItem", "ACCESS_TOKEN"].forEach((item) =>
          localStorage.removeItem(item),
        );
        navigate("/login");
      } else {
        enqueueSnackbar(<AlertMessages statusCode={response.status} />, {
          variant: "error",
        });
      }
    },
    onError: (err) => {
      console.error("Login error======", err);
      if (err instanceof Error) {
        console.error(err.stack);
      }
    },
  });
  const handleDropdownActions = (
    typeName: string,
    action: "searchIt" | "loadNext",
    search?: string,
  ) => {
    if (action === "searchIt") {
      setPageNo(1);
      setDropdownSearchTerm(search);
      if (search) setSearchLoading(typeName);
    } else {
      setPageNo(pageNo + 1);
      setSearchLoading(typeName);
    }
    if (debounceTimer.current) clearTimeout(debounceTimer.current);
    debounceTimer.current = setTimeout(() => {
      if (typeName === "taxProcedures")
        taxProceduresRefetch().finally(() => setSearchLoading(null));
      else if (typeName === "scrapItemGroups")
        scrapItemGroupsRefetch().finally(() => setSearchLoading(null));
      else if (typeName === "accounts")
        bankAccountsRefetch().finally(() => setSearchLoading(null));
      else if (typeName === "suppliers")
        vendorsRefetch().finally(() => setSearchLoading(null));
    }, 300);
  };
  return (
    <>
      <ComponentHeaderTwo
        showBtn={false}
        showSaveBtn={false}
        heading={t("accountSettings")}
        icon={<Accounts height={28} width={28} />}
        btnText="back"
        btnType="back"
        isBtnEnabled={IsBtnEnabled}
      />
      <div className={`flex`}>
        <AccountSettingsTabs selectedIndex={1} />
        {loading ? (
          <Loader />
        ) : (
          <SimpleModal
            visable={true}
            title={"editBusiness"}
            isButtonVisable={true}
            isVisableBtn={IsBtnEnabled}
            btnclosetext="Back"
            buttonText={"update"}
            attBtnNotshow={false}
            notVisableBackbtn={true}
            showPortList={handleSubmit(onSubmit)}
            formSubmit={handleSubmit(onSubmit)}
            content={
              <Grid
                container
                component="form"
                id="form"
                spacing={2}
                onSubmit={handleSubmit(onSubmit)}
              >
                <Grid item xs={12} md={3}>
                  <TextField
                    {...register("title", {
                      validate: {
                        required: (value) =>
                          value.trim() !== "" || t("This field is required."),
                      },
                    })}
                    label={`${!primaryLanguage ? t("primary") : ""} ${t("name")} ${primaryLanguage ? ` (${t(primaryLanguage)})` : ""} *`}
                    placeholder={`${!primaryLanguage ? t("primary") : ""} ${t("name")} ${primaryLanguage ? ` (${t(primaryLanguage)})` : ""} *`
                      .replace(/\s+/g, " ")
                      .trim()}
                    title={`${!primaryLanguage ? t("primary") : ""} ${t("name")} ${primaryLanguage ? ` (${t(primaryLanguage)})` : ""} *`
                      .replace(/\s+/g, " ")
                      .trim()}
                    fullWidth
                    error={!!errors.title}
                    helperText={errors.title?.message as string}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <TextField
                    {...register("secondaryTitle")}
                    label={`${!secondaryLanguage ? t("secondary") : ""} ${t("name")} ${secondaryLanguage ? ` (${t(secondaryLanguage)})` : ""}`}
                    placeholder={`${!secondaryLanguage ? t("secondary") : ""} ${t("name")} ${secondaryLanguage ? ` (${t(secondaryLanguage)})` : ""}`
                      .replace(/\s+/g, " ")
                      .trim()}
                    title={`${!secondaryLanguage ? t("secondary") : ""} ${t("name")} ${secondaryLanguage ? ` (${t(secondaryLanguage)})` : ""}`
                      .replace(/\s+/g, " ")
                      .trim()}
                    fullWidth
                    error={!!errors.secondaryTitle}
                    helperText={errors.secondaryTitle?.message as string}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <Controller
                    name="primaryLanguage"
                    control={control}
                    defaultValue={null}
                    rules={{ required: false }}
                    render={({ field }) => (
                      <DynamicSelect
                        label={t("primaryLanguage")}
                        {...field}
                        options={languageOptions}
                        isClearable
                        isSearchable
                        onChange={(selectedOption: any) =>
                          field.onChange(selectedOption)
                        }
                        error={errors.primaryLanguage?.message as string}
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <Controller
                    name="secondaryLanguage"
                    control={control}
                    defaultValue={null}
                    rules={{ required: false }}
                    render={({ field }) => (
                      <DynamicSelect
                        label={t("secondaryLanguage")}
                        {...field}
                        options={languageOptions}
                        isClearable
                        isSearchable
                        onChange={(selectedOption: any) =>
                          field.onChange(selectedOption)
                        }
                        error={errors.secondaryLanguage?.message as string}
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <Controller
                    name="currency"
                    control={control}
                    defaultValue={null}
                    rules={{ required: false }}
                    render={({ field }) => (
                      <DynamicSelect
                        label={t("currency")}
                        {...field}
                        options={currencyOptions}
                        isClearable
                        isSearchable
                        onChange={(selectedOption: any) =>
                          field.onChange(selectedOption)
                        }
                        error={errors.currency?.message as string}
                      />
                    )}
                  />
                </Grid>
                {/* <Grid item xs={12} md={3}>
                  <Controller
                    name="scrapItemGroupId"
                    control={control}
                    defaultValue={null}
                    rules={{ required: t("This field is required.") }}
                    render={({ field }) => (
                      <DynamicSelect
                        label={t("scrapItemGroup") + " *"}
                        {...field}
                        options={scrapItemGroups}
                        isClearable
                        isSearchable
                        onChange={(selectedOption: any) =>
                          field.onChange(selectedOption)
                        }
                        error={errors.scrapItemGroupId?.message as string}
                        isLoading={searchLoading === "scrapItemGroups"}
                        onInputChange={(
                          search: string,
                          { action }: { action: string },
                        ) => {
                          if (
                            action === "input-change" ||
                            (action !== "set-value" &&
                              action !== "menu-close" &&
                              action !== "input-blur")
                          )
                            handleDropdownActions(
                              "scrapItemGroups",
                              "searchIt",
                              search,
                            );
                          else if (
                            action === "input-blur" ||
                            action === "menu-close"
                          ) {
                            if (dropdownSearchTerm || pageNo > 1)
                              handleDropdownActions(
                                "scrapItemGroups",
                                "searchIt",
                                "",
                              );
                            else setDropdownSearchTerm("");
                            setPageNo(1);
                          }
                        }}
                        onMenuScrollToBottom={() => {
                          if (
                            !(scrapItemGroupsData?.pages[0]?.data as any).last
                          )
                            handleDropdownActions(
                              "scrapItemGroups",
                              "loadNext",
                            );
                        }}
                        page={pageNo}
                      />
                    )}
                  />
                </Grid> */}
                <Grid item xs={12} md={3}>
                  <Controller
                    name="taxProcedureId"
                    control={control}
                    defaultValue={null}
                    rules={{ required: false }}
                    render={({ field }) => (
                      <DynamicSelect
                        label={t("taxProcedureId")}
                        {...field}
                        options={taxProcedures}
                        isClearable
                        isSearchable
                        onChange={(selectedOption: any) =>
                          field.onChange(selectedOption)
                        }
                        error={errors.taxProcedureId?.message as string}
                        isLoading={searchLoading === "taxProcedures"}
                        onInputChange={(
                          search: string,
                          { action }: { action: string },
                        ) => {
                          if (
                            action === "input-change" ||
                            (action !== "set-value" &&
                              action !== "menu-close" &&
                              action !== "input-blur")
                          )
                            handleDropdownActions(
                              "taxProcedures",
                              "searchIt",
                              search,
                            );
                          else if (
                            action === "input-blur" ||
                            action === "menu-close"
                          ) {
                            if (dropdownSearchTerm || pageNo > 1)
                              handleDropdownActions(
                                "taxProcedures",
                                "searchIt",
                                "",
                              );
                            else setDropdownSearchTerm("");
                            setPageNo(1);
                          }
                        }}
                        onMenuScrollToBottom={() => {
                          if (!(taxProceduresData?.pages[0]?.data as any).last)
                            handleDropdownActions("taxProcedures", "loadNext");
                        }}
                        page={pageNo}
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <Controller
                    name="vendorId"
                    control={control}
                    defaultValue={null}
                    rules={{ required: t("This field is required.") }}
                    render={({ field }) => (
                      <DynamicSelect
                        label={t("vendors") + " *"}
                        {...field}
                        options={supplierOptions}
                        isClearable
                        isSearchable
                        onChange={(selectedOption: any) =>
                          field.onChange(selectedOption)
                        }
                        error={errors.vendorId?.message as string}
                        isLoading={searchLoading === "suppliers"}
                        onInputChange={(
                          search: string,
                          { action }: { action: string },
                        ) => {
                          if (
                            action === "input-change" ||
                            (action !== "set-value" &&
                              action !== "menu-close" &&
                              action !== "input-blur")
                          )
                            handleDropdownActions(
                              "suppliers",
                              "searchIt",
                              search,
                            );
                          else if (
                            action === "input-blur" ||
                            action === "menu-close"
                          ) {
                            if (dropdownSearchTerm || pageNo > 1)
                              handleDropdownActions(
                                "suppliers",
                                "searchIt",
                                "",
                              );
                            else setDropdownSearchTerm("");
                            setPageNo(1);
                          }
                        }}
                        onMenuScrollToBottom={() => {
                          if (!(vendorsData?.pages[0]?.data as any).last)
                            handleDropdownActions("suppliers", "loadNext");
                        }}
                        page={pageNo}
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <Controller
                    name="bankAccountId"
                    control={control}
                    defaultValue={null}
                    rules={{ required: t("This field is required.") }}
                    render={({ field }) => (
                      <DynamicSelect
                        label={t("ACCOUNT") + " *"}
                        {...field}
                        options={bankAccounts}
                        isClearable
                        isSearchable
                        onChange={(selectedOption: any) =>
                          field.onChange(selectedOption)
                        }
                        error={errors.bankAccountId?.message as string}
                        isLoading={searchLoading === "accounts"}
                        onInputChange={(
                          search: string,
                          { action }: { action: string },
                        ) => {
                          if (
                            action === "input-change" ||
                            (action !== "set-value" &&
                              action !== "menu-close" &&
                              action !== "input-blur")
                          )
                            handleDropdownActions(
                              "accounts",
                              "searchIt",
                              search,
                            );
                          else if (
                            action === "input-blur" ||
                            action === "menu-close"
                          ) {
                            if (dropdownSearchTerm || pageNo > 1)
                              handleDropdownActions("accounts", "searchIt", "");
                            else setDropdownSearchTerm("");
                            setPageNo(1);
                          }
                        }}
                        onMenuScrollToBottom={() => {
                          if (!(vendorsData?.pages[0]?.data as any).last)
                            handleDropdownActions("accounts", "loadNext");
                        }}
                        page={pageNo}
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <Controller
                    name="zone"
                    control={control}
                    defaultValue={null}
                    rules={{ required: t("This field is required.") }}
                    render={({ field }) => (
                      <DynamicSelect
                        label={t("timeZone") + " *"}
                        {...field}
                        options={timeZonesList}
                        isClearable
                        isSearchable
                        onChange={(selectedOption: any) =>
                          field.onChange(selectedOption)
                        }
                        error={errors.zone?.message as string}
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <TextField
                    type="number"
                    {...register("ownerCapital", {
                      required: t("This field is required."),
                      validate: (value) =>
                        value > 0 || t("amountGreaterThanZero"),
                    })}
                    label={t("ownerCapital") + " *"}
                    placeholder={t("ownerCapital")}
                    title={t("ownerCapital")}
                    fullWidth
                    error={!!errors.ownerCapital}
                    helperText={errors.ownerCapital?.message as string}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                {/* <Grid item xs={12} md={3}>
                  <Controller
                    name="sellBelowStock"
                    control={control}
                    defaultValue={false}
                    render={({ field }) => (
                      <CommonCheckbox
                        {...field}
                        label={t("Sell Below Stock")}
                        onChange={(
                          event: React.ChangeEvent<HTMLInputElement>,
                        ) => field.onChange(event.target.checked)}
                        checked={field.value}
                        color={themeColors?.primary}
                      />
                    )}
                  />
                </Grid> */}
                <Grid item xs={12}>
                  <h1
                    className="flex font-Saira font-[700] text-[17px] mb-0"
                    style={{ color: themeColors?.primary }}
                  >
                    {t("plateNumberFormat")}
                  </h1>
                </Grid>
                <Grid item xs={12} md={3}>
                  <Controller
                    name="vehiclePlateFormatRegion"
                    control={control}
                    defaultValue={null}
                    rules={{ required: false }}
                    render={({ field }) => (
                      <DynamicSelect
                        label={t("region")}
                        {...field}
                        options={regionOptions}
                        isSearchable
                        onChange={(selectedOption: any) => {
                          field.onChange(selectedOption);
                          setValue(
                            "vehiclePlateFormat",
                            (regionFormatMap as any)[selectedOption.value].map(
                              (item: any) => ({
                                ...item,
                                type: {
                                  value: item.type.value,
                                  label: t(item.type.value),
                                },
                              }),
                            ),
                          );
                        }}
                        error={errors.currency?.message as string}
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={12} md={9}>
                  <Box
                    style={{
                      border: "2px solid",
                      borderColor: themeColors?.primary,
                      padding: "5px 12px",
                      width: "fit-content",
                      borderRadius: "8px",
                      display: "block",
                      marginLeft: "auto",
                    }}
                  >
                    {watch("vehiclePlateFormatRegion")?.value !== "uae" && (
                      <Typography
                        className="text-uppercase font-Saira text-[15px] font-medium"
                        style={{
                          letterSpacing: "2px",
                          color: "rgb(52, 52, 52)",
                        }}
                      >
                        {generatePlatePreview("ar")}
                      </Typography>
                    )}
                    {watch("vehiclePlateFormatRegion")?.value !== "uae" && (
                      <Divider
                        style={{
                          background: themeColors?.primary,
                          height: "2px",
                          margin: "3px 0",
                        }}
                      />
                    )}
                    <Typography
                      className="text-uppercase font-Saira text-[15px] font-medium spacing"
                      style={{ letterSpacing: "2px", color: "rgb(52, 52, 52)" }}
                    >
                      {generatePlatePreview("en")}
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12}>
                  {fields.map((field, index) => (
                    <Grid container spacing={2} key={field.id} className="mb-4">
                      <Grid item xs={12} sm={3}>
                        <TextField
                          label={t("title") + " *"}
                          InputLabelProps={{ shrink: true }}
                          placeholder={t("title")}
                          title={t("title")}
                          {...register(`vehiclePlateFormat.${index}.title`, {
                            required: t("This field is required."),
                          })}
                          fullWidth
                          error={
                            !!(errors as any).vehiclePlateFormat?.[index]?.title
                          }
                          helperText={
                            (errors as any).vehiclePlateFormat?.[index]?.title
                              ?.message as string
                          }
                          InputProps={{
                            readOnly:
                              watch("vehiclePlateFormatRegion")?.value !==
                              "custom",
                          }}
                        />
                      </Grid>
                      <Grid
                        item
                        xs={12}
                        sm={3}
                        sx={{ "& .commonSelect": { opacity: "1 !important" } }}
                      >
                        <Controller
                          control={control}
                          name={`vehiclePlateFormat.${index}.type`}
                          rules={{ required: t("This field is required.") }}
                          render={({ field }) => (
                            <DynamicSelect
                              {...field}
                              label={t("type") + " *"}
                              options={dataTypes}
                              error={
                                (errors as any).vehiclePlateFormat?.[index]
                                  ?.type?.message as string
                              }
                              isDisabled={
                                watch("vehiclePlateFormatRegion")?.value !==
                                "custom"
                              }
                            />
                          )}
                        />
                      </Grid>
                      <Grid item xs={12} sm={2}>
                        <TextField
                          label={t("min") + " *"}
                          InputLabelProps={{ shrink: true }}
                          placeholder={t("min")}
                          title={t("min")}
                          type="number"
                          inputProps={{ min: 1, step: 1 }}
                          {...register(`vehiclePlateFormat.${index}.min`, {
                            required: t("required"),
                            min: { value: 1, message: t("minOne") },
                          })}
                          fullWidth
                          error={
                            !!(errors as any).vehiclePlateFormat?.[index]?.min
                          }
                          helperText={
                            (errors as any).vehiclePlateFormat?.[index]?.min
                              ?.message as string
                          }
                          InputProps={{
                            readOnly:
                              watch("vehiclePlateFormatRegion")?.value !==
                              "custom",
                          }}
                        />
                      </Grid>
                      <Grid item xs={12} sm={2}>
                        <TextField
                          label={t("max") + " *"}
                          InputLabelProps={{ shrink: true }}
                          placeholder={t("max")}
                          title={t("max")}
                          type="number"
                          inputProps={{ min: 1, max: 15, step: 1 }}
                          {...register(`vehiclePlateFormat.${index}.max`, {
                            required: t("required"),
                            max: { value: 15, message: t("max15") },
                            validate: (val) =>
                              val >=
                                Number(
                                  watch(`vehiclePlateFormat.${index}.min`),
                                ) || t("mustBeGreaterThanMin"),
                          })}
                          onInput={(e: any) => {
                            if (e.target.value > 15) e.target.value = 15;
                          }}
                          fullWidth
                          error={
                            !!(errors as any).vehiclePlateFormat?.[index]?.max
                          }
                          helperText={
                            (errors as any).vehiclePlateFormat?.[index]?.max
                              ?.message as string
                          }
                          InputProps={{
                            readOnly:
                              watch("vehiclePlateFormatRegion")?.value !==
                              "custom",
                          }}
                        />
                      </Grid>
                      <Grid
                        item
                        xs={12}
                        sm={2}
                        display="flex"
                        alignItems="center"
                        gap={1}
                      >
                        {watch("vehiclePlateFormatRegion")?.value ===
                          "custom" &&
                          fields.length < 4 && (
                            <IconButton
                              onClick={() =>
                                append({
                                  title: "Plate Number",
                                  type: { value: "text", label: t("text") },
                                  min: 1,
                                  max: 15,
                                })
                              }
                              className="bg-[#E1E8E3] p-[5px] rounded-[8px] mt-[8px]"
                            >
                              <Add style={{ color: themeColors?.primary }} />
                            </IconButton>
                          )}
                        {watch("vehiclePlateFormatRegion")?.value ===
                          "custom" &&
                          fields.length > 1 && (
                            <IconButton
                              onClick={() => remove(index)}
                              color="error"
                              className="bg-[#E1E8E3] p-[5px] rounded-[8px] mt-[8px]"
                            >
                              <Remove />
                            </IconButton>
                          )}
                      </Grid>
                    </Grid>
                  ))}
                </Grid>
                <Grid item xs={12}>
                  <h1
                    className="flex font-Saira font-[700] text-[17px] mb-0"
                    style={{ color: themeColors?.primary }}
                  >
                    {t("showDefaultValues")}
                  </h1>
                </Grid>
                <Grid item xs={12}>
                  <Grid container>
                    {Object.keys(
                      loggedInUserData?.business?.defaultEntity || {},
                    ).map((key) => (
                      <Grid item xs={12} sm={6} md={4} lg={3} key={key}>
                        <Controller
                          name={`defaultEntity.${key}`}
                          control={control}
                          render={({ field }) => (
                            <Box
                              sx={{
                                "& label": { paddingTop: "0px !important" },
                                "& .checkboxerror": {
                                  display: "none !important",
                                },
                              }}
                            >
                              <CommonCheckbox
                                {...field}
                                label={t(key)}
                                checked={field.value}
                                onChange={(
                                  e: React.ChangeEvent<HTMLInputElement>,
                                ) => field.onChange(e.target.checked)}
                                color={themeColors?.primary}
                              />
                            </Box>
                          )}
                        />
                      </Grid>
                    ))}
                  </Grid>
                </Grid>
              </Grid>
            }
          />
        )}
      </div>
      {updateBusinessModal && (
        <FormDialog
          open={updateBusinessModal}
          onClose={(event: any, reason: any) => {
            if (event && reason !== "backdropClick") {
              setUpdateBusinessModal(false);
              setIsBtnEnabled(true);
            }
          }}
          title={t("update") + " " + t("business") + " " + t("settings")}
          content={
            <Grid container>
              <Grid item xs={12}>
                <Typography>{t("applyBusinessSettingsMessage")}</Typography>
              </Grid>
            </Grid>
          }
          saveFunction={() => {
            BusinessProfileService.updateBusiness(
              loggedInUserData?.business?.id,
              business,
            )
              .then((response: any) => {
                if (response.success) {
                  enqueueSnackbar(
                    <AlertMessages
                      statusCode={response.status}
                      text={t("updated") + " " + t("successfully")}
                    />,
                    { variant: "success" },
                  );
                  setUpdateBusinessModal(false);
                  localStorage.getItem("ACCESS_TOKEN")
                    ? onLogOut()
                    : navigate("/login");
                } else {
                  setIsBtnEnabled(true);
                  enqueueSnackbar(<AlertMessages text={response?.message} />, {
                    variant: "error",
                  });
                }
              })
              .catch((err: any) => {
                console.error("===", err);
                setIsBtnEnabled(true);
                enqueueSnackbar(
                  <AlertMessages
                    text={
                      err?.message ||
                      err?.data?.message ||
                      "Something went wrong"
                    }
                  />,
                  { variant: "error" },
                );
              });
          }}
          submitButtonText="OK"
        />
      )}
    </>
  );
}
